// LoginLeftPanel — shared visual panel used on the left of every "-new" auth
// page (Login-new, MagicLinkVerify-new, MagicLinkLanding-new, VerifyInvitation-new,
// SetPassword-new). Carousel: when SLIDES has >1 entry it auto-advances every
// SLIDE_INTERVAL_MS with click-to-jump dots; when there's only 1 slide the
// interval and dot indicator are skipped (static panel).
//
// Centre illustration: Lottie animation (new-login.json). The static PNG export
// (Login_static.png) is the fallback whenever Lottie is missing, fails to parse,
// or throws at render time — so the page is never broken visually.
import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';

// Lazy-fail import: if the JSON file is missing/corrupt at build time webpack
// would throw, but we still defensively coerce a falsy / structurally-broken
// payload to "use fallback" via the lottieFailed state below.
import useLottieData from '../../hooks/useLottieData';

const STATIC_FALLBACK_SRC = '/images/login-new/Login_static.png';

// Slide content. `description` is an array of paragraphs so we can render a
// visible blank line between them without a CSS change. Add more entries to
// SLIDES to re-enable the auto-advance carousel + dot indicator.
const SLIDES = [
	{
		tagline: "Listen to what's forming.",
		description: [
			'AI-assisted conversational interviews with real people - designed to help organizations understand emerging attitudes, behaviors, and cultural change.',
			'Built on human conversation, not synthetic data.',
		],
	},
];

const SLIDE_INTERVAL_MS = 5000;

// Quick structural check: a valid Lottie JSON has v, w, h and a non-empty layers array.
// If any of these is missing we treat the asset as broken and show the static PNG.
const isValidLottie = (data) =>
	!!data && typeof data === 'object' && Array.isArray(data.layers) && data.layers.length > 0;

const LoginLeftPanel = () => {
	const [currentSlide, setCurrentSlide] = useState(0);
	// Start in the "use Lottie" state if the JSON loaded AND looks structurally
	// sane. Flip to true if Lottie's onError fires at runtime (parse / render
	// failure) — the static PNG is rendered in that case.
	const newLoginLottie = useLottieData('/lottie/new-login.json');
	const [lottieFailed, setLottieFailed] = useState(false);

	useEffect(() => {
		if (SLIDES.length <= 1) return undefined;
		const id = setInterval(() => {
			setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
		}, SLIDE_INTERVAL_MS);
		return () => clearInterval(id);
	}, []);

	const slide = SLIDES[currentSlide];

	return (
		<div className='loginLeftCard'>
			<div className='loginRings' aria-hidden='true'>
				{lottieFailed || !isValidLottie(newLoginLottie) ? (
					<img src={STATIC_FALLBACK_SRC} alt='' />
				) : (
					<Lottie
						animationData={newLoginLottie}
						loop
						autoplay
						style={{ width: '100%', height: '100%' }}
						onError={() => setLottieFailed(true)}
					/>
				)}
			</div>
			<div className='loginTaglineGroup'>
				<p key={`tagline-${currentSlide}`} className='loginTagline'>
					{slide.tagline}
				</p>
				<p key={`desc-${currentSlide}`} className='loginDescription'>
					{(Array.isArray(slide.description) ? slide.description : [slide.description]).map((para, i) => (
						<React.Fragment key={i}>
							{i > 0 && (
								<>
									<br />
									<br />
								</>
							)}
							{para}
						</React.Fragment>
					))}
				</p>
				{SLIDES.length > 1 && (
					<div className='loginDots'>
						{SLIDES.map((_, i) => (
							<img
								key={i}
								src={
									i === currentSlide
										? '/images/login-new/dot-active.svg'
										: '/images/login-new/dot-inactive.svg'
								}
								alt=''
								onClick={() => setCurrentSlide(i)}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default LoginLeftPanel;
